import { useState } from "react";

import { Button, PricingBillingPeriodPicker, PricingCard } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import useNavigation from "../../hooks/navigation";
import { type TierPriceConfig, usePriceConfig } from "../../hooks/usePriceConfig";
import useTranslation from "../../hooks/useTranslation";
import { SubscriptionPeriod, SubscriptionTier } from "../../types";
import { formatCurrency, getTaxLabel } from "../../utils/currencyUtils";
import { getWebAppPricingConfigs } from "./pricingConfigs";

import style from "./PricingCardsSection.module.css";

export const PricingCardsSection: React.FC = () => {
  const { data: priceConfig } = usePriceConfig();
  const navigation = useNavigation();
  const { t, m } = useTranslation();
  const configs = getWebAppPricingConfigs(t);
  const [period, setPeriod] = useState<SubscriptionPeriod>(SubscriptionPeriod.Yearly);

  const isYearly = period === SubscriptionPeriod.Yearly;

  function getPriceDisplay(tierConfig: TierPriceConfig | undefined) {
    if (!priceConfig || !tierConfig) return undefined;
    return isYearly
      ? formatCurrency(Math.round((tierConfig.yearly / 12) * 100) / 100, priceConfig.currencyCode)
      : formatCurrency(tierConfig.monthly, priceConfig.currencyCode);
  }

  function getSubtitle(tierConfig: TierPriceConfig | undefined) {
    if (!priceConfig || !tierConfig) return undefined;
    const taxLabel = getTaxLabel(priceConfig.currencySymbol);
    if (!isYearly) return t(m.Purchase.PriceBilledMonthly, { taxLabel });
    return t(m.Purchase.PriceBilledAnnuallyWithPrice, {
      price: formatCurrency(tierConfig.yearly, priceConfig.currencyCode),
      billedAnnually: t(m.Purchase.PriceBilledAnnually, { taxLabel }),
    });
  }

  const individualTier = priceConfig?.tiers[SubscriptionTier.Individual];
  const studioTier = priceConfig?.tiers[SubscriptionTier.Studio];

  const savingsText = individualTier
    ? t(m.Purchase.PriceMonthlySave, { savingsPercent: Math.round(individualTier.savingsPercent) })
    : undefined;

  function withCurrency(price: React.ReactNode) {
    if (typeof price !== "string") return price;
    return priceConfig ? price.replace("$", priceConfig.currencySymbol) : price;
  }

  return (
    <div className={style.section}>
      <div className={style.cardsWrapper}>
        <PricingBillingPeriodPicker
          isYearly={isYearly}
          onYearlyChange={(next) =>
            setPeriod(next ? SubscriptionPeriod.Yearly : SubscriptionPeriod.Monthly)
          }
          monthlyLabel={t(m.Button.Monthly)}
          yearlyLabel={t(m.Button.Yearly)}
          yearlyExtra={savingsText}
        />
        <div className={style.cards}>
          <PricingCard
            {...configs.free}
            price={withCurrency(configs.free.price)}
            reserveSubtitleSpace
            reserveTaglineSpace
            ctaSlot={
              <Button
                type="button"
                variant="grayscale"
                disabled
                aria-current="true"
                className={classNames(style.cardCta)}
              >
                {t(m.PricingCards.Free.Tagline)}
              </Button>
            }
            className={style.cardFree}
          />
          <PricingCard
            {...configs.individual}
            price={getPriceDisplay(individualTier)}
            subtitle={getSubtitle(individualTier)}
            ctaSlot={
              <Button
                variant="primary"
                className={style.cardCta}
                onClick={() =>
                  navigation.goToCheckout({ selectedTier: SubscriptionTier.Individual, period })
                }
              >
                {t(m.Checkout.SubscribeNow)}
              </Button>
            }
            className={style.cardIndividual}
          />
          <PricingCard
            {...configs.studio}
            price={getPriceDisplay(studioTier)}
            subtitle={getSubtitle(studioTier)}
            ctaSlot={
              <Button
                variant="grayscale"
                className={style.cardCta}
                onClick={() =>
                  navigation.goToCheckout({ selectedTier: SubscriptionTier.Studio, period })
                }
              >
                {t(m.Checkout.SubscribeNow)}
              </Button>
            }
            className={style.cardStudio}
          />
          <PricingCard
            {...configs.enterprise}
            price="Contact Sales"
            pricePrefix={undefined}
            pricePeriod=""
            priceSubline={undefined}
            subtitle={undefined}
            ctaSlot={
              <Button
                variant="grayscale"
                className={style.cardCta}
                style={{ isolation: "isolate" }}
                onClick={() => {
                  window.open(
                    "https://www.shapr3d.com/enterprise",
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              >
                {t(m.Button.ContactUs)}
              </Button>
            }
            className={style.cardEnterprise}
          />
        </div>
      </div>
    </div>
  );
};
